import { Puck, type Data } from '@puckeditor/core';
import { buildConfig } from '@nailthelanding/blocks/config';
import '@puckeditor/core/puck.css';
// Default values for all --color-*, --font-*, --space-*, --radius-*, --shadow-*,
// --motion-* tokens that the block components consume. The per-site theme.css
// overrides individual tokens; tokens.css provides the fallbacks.
import '@nailthelanding/blocks/tokens.css';
// Component styles (CSS Modules bundled during the blocks library build).
// Needed because Tailwind's preflight resets heading sizes, list styles, etc.,
// and our components rely on their module CSS for layout.
import '@nailthelanding/blocks/style.css';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { uploadImage } from './api';
import styles from './SiteEditor.module.css';

export type SiteEditorMode = 'new' | 'existing';

export type EditorViewport = 'desktop' | 'tablet' | 'mobile';

export type SiteEditorProps = {
  mode: SiteEditorMode;
  initialData: Data;
  themeCss: string;
  customCss: string;
  /** Initial viewport the editor opens in. Read from `meta.editorViewport`. */
  editorViewport?: EditorViewport;
  onChange?: (data: Data) => void;
};

const VIEWPORTS = [
  { width: 1280, height: 'auto' as const, label: 'Desktop' },
  { width: 768,  height: 'auto' as const, label: 'Tablet'  },
  { width: 375,  height: 'auto' as const, label: 'Mobile'  },
];

const VIEWPORT_BY_NAME: Record<EditorViewport, (typeof VIEWPORTS)[number]> = {
  desktop: VIEWPORTS[0],
  tablet:  VIEWPORTS[1],
  mobile:  VIEWPORTS[2],
};

/**
 * Inject the per-site theme + custom CSS into Puck's iframe document — NOT
 * the parent document — so the template's tokens (`--font-*`, `--color-*`,
 * `html, body { ... }`, `::selection`, etc.) cannot leak into cloud-landing's
 * chrome. Mounted via Puck's `overrides.iframe`, which hands us the iframe's
 * own `Document` once it's ready.
 */
type IframeStyleInjectorProps = {
  document: Document | undefined;
  themeCss: string;
  customCss: string;
  children: ReactNode;
};

const IframeStyleInjector = ({
  document: iframeDoc,
  themeCss,
  customCss,
  children,
}: IframeStyleInjectorProps) => {
  useEffect(() => {
    if (!iframeDoc) return;

    // Append (idempotent) the per-site theme + custom CSS at the END of the
    // iframe head — *after* whatever Puck's AutoFrame has mirrored from the
    // parent document. That ordering matters: theme.css `:root` overrides
    // must come after the blocks library's tokens.css for the per-site
    // values to win on equal specificity.
    const ensureStyles = () => {
      let theme = iframeDoc.querySelector(
        'style[data-site-editor="theme"]',
      ) as HTMLStyleElement | null;
      if (!theme) {
        theme = iframeDoc.createElement('style');
        theme.setAttribute('data-site-editor', 'theme');
        iframeDoc.head.appendChild(theme);
      } else if (theme !== iframeDoc.head.lastElementChild?.previousElementSibling) {
        // Already present but no longer at the tail — re-append to keep
        // override ordering correct.
        iframeDoc.head.appendChild(theme);
      }
      if (theme.textContent !== themeCss) theme.textContent = themeCss;

      let custom = iframeDoc.querySelector(
        'style[data-site-editor="custom"]',
      ) as HTMLStyleElement | null;
      if (!custom) {
        custom = iframeDoc.createElement('style');
        custom.setAttribute('data-site-editor', 'custom');
        iframeDoc.head.appendChild(custom);
      } else if (custom !== iframeDoc.head.lastElementChild) {
        iframeDoc.head.appendChild(custom);
      }
      if (custom.textContent !== customCss) custom.textContent = customCss;
    };

    ensureStyles();

    // Puck's AutoFrame does `head.innerHTML = ""` and re-appends mirrored
    // styles asynchronously after we've already injected. Watch the iframe
    // head for that wipe and re-inject.
    const observer = new MutationObserver(() => ensureStyles());
    observer.observe(iframeDoc.head, { childList: true });

    return () => {
      observer.disconnect();
      iframeDoc
        .querySelectorAll('style[data-site-editor]')
        .forEach((el) => el.remove());
    };
  }, [iframeDoc, themeCss, customCss]);

  return <>{children}</>;
};

export const SiteEditor = ({
  initialData,
  themeCss,
  customCss,
  editorViewport,
  onChange,
}: SiteEditorProps) => {
  // Capture the initial Puck data ONCE. Passing `initialData` straight through
  // as a controlled `data` prop makes Puck's `onChange` feedback loop with the
  // parent's state and trigger "Maximum update depth exceeded" under React 19
  // strict mode. Puck treats `data` as the initial state; after that Puck owns
  // it internally and reports changes via `onChange`. To force a reset, the
  // caller must remount `<SiteEditor>` via a different React `key`.
  const [capturedData] = useState(() => initialData);

  const config = useMemo(
    () => buildConfig({ upload: uploadImage }),
    [],
  );

  const initialViewport =
    (editorViewport && VIEWPORT_BY_NAME[editorViewport]) ?? VIEWPORTS[0];

  const overrides = useMemo(
    () => ({
      // Puck's default header includes a Publish action. Publishing in this
      // app is owned by the wizard's top-right Publish button.
      headerActions: () => <></>,
      // Inject per-site theme.css + custom.css into Puck's iframe document
      // only, so its `:root` tokens, `html, body { ... }` rules, etc. cannot
      // bleed into cloud-landing's chrome.
      iframe: ({ children, document: iframeDoc }: { children: ReactNode; document?: Document }) => (
        <IframeStyleInjector document={iframeDoc} themeCss={themeCss} customCss={customCss}>
          {children}
        </IframeStyleInjector>
      ),
    }),
    [themeCss, customCss],
  );

  return (
    <div className={styles.root}>
      <Puck
        config={config}
        data={capturedData}
        onChange={onChange}
        height="100%"
        iframe={{ enabled: true, waitForStyles: true }}
        viewports={VIEWPORTS}
        ui={{
          viewports: {
            current: { width: initialViewport.width, height: initialViewport.height },
            controlsVisible: true,
            options: VIEWPORTS,
          },
        }}
        overrides={overrides}
      />
    </div>
  );
};
